import React, { FC, useMemo } from 'react'
import classNames from 'classnames'
import { FlexColumn } from '@/atoms/FlexContainers'
import { CircleCheckIcon } from '@/atoms/Icons/CircleCheckIcon'
import { GuildIcon } from '@/atoms/Icons/GuildIcon'
import { PayItForwardOutlineIcon } from '@/atoms/Icons/PayItForwardOutlineIcon'
import { PlaySquareIcon } from '@/atoms/Icons/PlaySquareIcon'
import { CaptionMD, TitleMD } from '@/atoms/Text'
import { IconColor } from '@/atoms/utils'
import { slugs } from '@/constants/slugs'
import { usePayItForwardOfferData } from '@/contexts/PayItForwardOffersDataContext'
import { useThemeContext } from '@/contexts/ThemeContext'
import { useGuildMember } from '@/services/GuildUserService'
import { Translate, useTranslate } from '@/utils/translate/translate-client'

const HOMESTEAD_EPISODE_PERK_PRODUCT_ID = '546'
const PIF_PERK_PRODUCT_ID = '282'

interface Props {
  theatricalSlug: string
  guildMembershipIncluded?: boolean
}
export const slugsWithExclusiveScene = new Set([
  slugs.soundOfHopeTheStoryOfPossumTrot,
  slugs.sight,
  slugs.bonhoeffer,
  slugs.kingOfKings,
])

export const ExpressPerks: FC<Props> = ({ theatricalSlug, guildMembershipIncluded = false }) => {
  const { t } = useTranslate('pif')
  const { selectedOffer } = usePayItForwardOfferData()
  const { isDarkMode } = useThemeContext()
  const { isGuildMember } = useGuildMember()

  const perks = useMemo(() => selectedOffer?.offeredproductSet, [selectedOffer?.offeredproductSet])
  return (
    <FlexColumn className="gap-4">
      <TitleMD color={isDarkMode ? 'white' : 'core-gray-950'} weight="semibold">
        {t('yourImpactAndPerks', 'Your Impact and Perks')}
      </TitleMD>
      <ul className="relative flex flex-col  gap-3">
        {perks?.map((perk) => {
          if (!perk) return null

          return <Perk theatricalSlug={theatricalSlug} productId={perk.product.id} key={perk.id} text={perk.name} />
        })}
        {slugsWithExclusiveScene.has(theatricalSlug) && (
          <Perk
            theatricalSlug={theatricalSlug}
            productId="exclusiveNewScenePerkExpressV2"
            text={t('exclusiveNewScenePerkExpressV2', 'Exclusive new scene delivered by email.')}
          />
        )}
        {theatricalSlug === slugs.kingOfKings && (
          <>
            <Perk
              theatricalSlug={theatricalSlug}
              productId="kingOfKingsDigitalPoster"
              text={t('kingOfKingsDigitalPosterTKOK', 'Exclusive digital poster')}
            />
            <Perk
              theatricalSlug={theatricalSlug}
              productId="kingOfKingsColoringPages"
              text={t('kingOfKingsColoringPagesTKOK', 'Coloring pages')}
            />
          </>
        )}
        {theatricalSlug === 'homestead' && !isGuildMember && (
          <Perk
            theatricalSlug={theatricalSlug}
            productId="homesteadGuildUpsellFrontEndHardCodedProductId"
            enabled={guildMembershipIncluded}
            text={undefined}
          />
        )}
      </ul>
    </FlexColumn>
  )
}

export const Perk = ({
  text,
  productId,
  enabled = true,
  theatricalSlug,
}: {
  text: string | undefined
  productId: string
  theatricalSlug: string
  enabled?: boolean
  isVerticalProgressBar?: boolean
}) => {
  const { t } = useTranslate('pif')
  const { isDarkMode } = useThemeContext()

  const isHomesteadGuildUpsell = productId === 'homesteadGuildUpsellFrontEndHardCodedProductId'

  return (
    <li className={classNames('flex items-center gap-2', { 'opacity-50': !enabled })}>
      <PerkIcon productId={productId} enabled={enabled} isDarkMode={isDarkMode} theatricalSlug={theatricalSlug} />
      <CaptionMD color={isDarkMode ? 'core-gray-200' : 'core-gray-800'}>
        {isHomesteadGuildUpsell ? (
          <Translate
            t={t}
            i18nKey="instantAccessToHomesteadAndTicketsBold"
            defaults="Instant access to <bold>Homestead Episode 2</bold> and the <bold>ENTIRE</bold> Angel Studios library."
            components={{ bold: <b /> }}
          />
        ) : (
          text
        )}
      </CaptionMD>
    </li>
  )
}

const PerkIcon = ({
  productId,
  enabled,
  isDarkMode,
}: {
  productId: string
  enabled: boolean
  isDarkMode: boolean
  theatricalSlug: string
}) => {
  const icon = getIcon(productId, enabled, isDarkMode)

  if (icon) {
    return icon
  }

  if (enabled) {
    return (
      <CircleCheckIcon
        className="mb-auto shrink-0 leading-[1.125rem] sm:my-0"
        size={24}
        color1={isDarkMode ? 'oxide-bright' : 'oxide-primary'}
        color2={isDarkMode ? 'core-gray-950' : 'white'}
      />
    )
  }

  return (
    <CircleCheckIcon
      className="mb-auto shrink-0 leading-[1.125rem] sm:my-0"
      size={24}
      color1="core-gray-400"
      color2="core-gray-400"
    />
  )
}

const getIcon = (productId: string, enabled: boolean, isDarkMode: boolean) => {
  if (productId === PIF_PERK_PRODUCT_ID) {
    return <PayItForwardOutlineIcon size={24} color={isDarkMode ? 'oxide-bright' : 'oxide-primary'} />
  }

  if (productId === HOMESTEAD_EPISODE_PERK_PRODUCT_ID || productId === 'exclusiveNewScenePerkExpressV2') {
    return <PlaySquareIcon size={24} color={isDarkMode ? 'oxide-bright' : 'oxide-primary'} />
  }

  if (productId === 'homesteadGuildUpsellFrontEndHardCodedProductId') {
    const [darkColor, lightColor] = (
      enabled ? ['oxide-bright', 'oxide-primary'] : ['core-gray-400', 'core-gray-400']
    ) as [IconColor, IconColor]

    return (
      <GuildIcon size={26} color1={isDarkMode ? darkColor : lightColor} color2={isDarkMode ? darkColor : lightColor} />
    )
  }

  return null
}
